import { DeleteOutline } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Chip,
  alpha,
} from '@mui/material';
import type {
  IBuilderTransactionOption,
  ILegalDependencyActionRowProps,
  PhaseStatus,
} from '../../../Interfaces/ILegalDependency';
import { HierarchicalTargetAutocomplete } from './HierarchicalTargetAutocomplete';
import { PHASE_STATUS_OPTIONS, getStatusFieldSx } from './utils';

export const LegalDependencyActionRow = ({
  action,
  getRequirementOption,
  getStatusLabel,
  getTransactionOption,
  isLoadingLegalTransactions,
  legalRequirementStates,
  onChange,
  onRemove,
  removeDisabled,
  requirementOptions,
  transactionLoading,
  transactionOptions,
}: ILegalDependencyActionRowProps) => {
  const selectedRequirementHasError =
    action.targetType === 'requirement' && action.idLegalRequirement !== null
      ? (requirementOptions.find(
          o => o.idLegalRequirement === action.idLegalRequirement
        )?.hasError ?? true)
      : true;
  const filteredStates = selectedRequirementHasError
    ? legalRequirementStates
    : legalRequirementStates.filter(s => s.idLegalRequirementState !== 30);

  return (
    <Box
      sx={{
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 2.5,
        p: 1.75,
        bgcolor: theme => alpha(theme.palette.success.light, 0.04),
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          alignItems='center'
          direction='row'
          justifyContent='space-between'
        >
          <Chip label='Aktion' size='small' variant='outlined' />
          {transactionLoading ? <CircularProgress size={18} /> : null}
        </Stack>

        <Box sx={{ px: 1.5 }}>
          <Grid alignItems='center' container spacing={1.5}>
            <Grid item xs={12} lg={4}>
              <Autocomplete<IBuilderTransactionOption, false, false, false>
                disabled={isLoadingLegalTransactions}
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(_, value) =>
                  onChange({
                    transactionId: value?.value ?? null,
                    idLegalRequirement: null,
                    idLegalPhase: null,
                    targetStateId: null,
                    TargetPhaseIsCompleted: null,
                  })
                }
                options={transactionOptions}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Vorgang'
                    placeholder='Vorgang suchen'
                    size='small'
                  />
                )}
                size='small'
                value={getTransactionOption(action.transactionId)}
              />
            </Grid>
            <Grid item xs={12} lg={5}>
              <HierarchicalTargetAutocomplete
                disabled={action.transactionId === null || transactionLoading}
                label='Bezug'
                noOptionsText={
                  action.transactionId === null
                    ? 'Bitte zuerst einen Vorgang wählen'
                    : 'Keine passenden Phasen oder Maßnahmen gefunden'
                }
                onChange={value =>
                  onChange({
                    targetType: value?.targetType ?? action.targetType,
                    idLegalRequirement: value?.idLegalRequirement ?? null,
                    idLegalPhase: value?.idLegalPhase ?? null,
                    targetStateId: null,
                    TargetPhaseIsCompleted: null,
                  })
                }
                options={requirementOptions}
                placeholder='Phase oder Maßnahme suchen'
                value={getRequirementOption(
                  action.transactionId,
                  action.targetType,
                  action.idLegalRequirement,
                  action.idLegalPhase
                )}
              />
            </Grid>
            <Grid item xs={12} lg={2}>
              <TextField
                disabled={
                  action.transactionId === null ||
                  (action.targetType === 'requirement'
                    ? action.idLegalRequirement === null
                    : action.idLegalPhase === null)
                }
                fullWidth
                label='Zielstatus'
                onChange={event =>
                  onChange(
                    action.targetType === 'requirement'
                      ? {
                          targetStateId:
                            event.target.value === ''
                              ? null
                              : Number(event.target.value),
                          TargetPhaseIsCompleted: null,
                        }
                      : {
                          targetStateId: null,
                          TargetPhaseIsCompleted:
                            event.target.value === ''
                              ? null
                              : (Number(event.target.value) as PhaseStatus),
                        }
                  )
                }
                select
                size='small'
                sx={getStatusFieldSx(
                  getStatusLabel(
                    action.targetType,
                    action.targetStateId,
                    action.TargetPhaseIsCompleted
                  )
                )}
                value={
                  action.targetType === 'requirement'
                    ? (action.targetStateId ?? '')
                    : (action.TargetPhaseIsCompleted ?? '')
                }
              >
                <MenuItem value=''>Status wählen</MenuItem>
                {action.targetType === 'requirement'
                  ? filteredStates.map(state => (
                      <MenuItem
                        key={state.idLegalRequirementState}
                        value={state.idLegalRequirementState}
                      >
                        {state.LegalRequirementState}
                      </MenuItem>
                    ))
                  : PHASE_STATUS_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
              </TextField>
            </Grid>
            <Grid item xs={12} lg={1}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-end', lg: 'center' },
                }}
              >
                <IconButton disabled={removeDisabled} onClick={onRemove}>
                  <DeleteOutline />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
};
